import { useMemo, useState } from 'react';
import { Loader2, Plus, Search, ShieldCheck, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { useUpdateEmployeePermissions } from '@/features/teams/api/employees';
import type { EmployeeDetail } from '@/features/teams/types';
import { KNOWN_PERMISSION_KEYS, PERMISSION_GROUPS } from './permissionCatalog';

type EmployeePermissionUser = Pick<EmployeeDetail, 'id' | 'permission_strings'>;

interface EmployeePermissionsPanelProps {
  employee: EmployeePermissionUser;
}

function normalizePermission(value: string): string {
  return value.trim();
}

function nextPermissions(current: string[], key: string, enabled: boolean): string[] {
  if (enabled) {
    return current.includes(key) ? current : [...current, key].sort();
  }
  return current.filter((permission) => permission !== key);
}

function nextGroupPermissions(current: string[], keys: string[], enabled: boolean): string[] {
  if (enabled) {
    return Array.from(new Set([...current, ...keys])).sort();
  }

  const keysToRemove = new Set(keys);
  return current.filter((permission) => !keysToRemove.has(permission));
}

export function EmployeePermissionsPanel({ employee }: EmployeePermissionsPanelProps) {
  const updatePermissions = useUpdateEmployeePermissions();
  const [search, setSearch] = useState('');
  const [customPermission, setCustomPermission] = useState('');

  const assignedPermissions = useMemo(() => employee.permission_strings ?? [], [employee.permission_strings]);
  const assignedSet = useMemo(() => new Set(assignedPermissions), [assignedPermissions]);
  const pendingPermission = updatePermissions.variables?.permissionKey;
  const normalizedSearch = search.trim().toLowerCase();

  const visibleGroups = useMemo(() => {
    if (!normalizedSearch) return PERMISSION_GROUPS;
    return PERMISSION_GROUPS
      .map((group) => ({
        ...group,
        permissions: group.permissions.filter((permission) => (
          group.label.toLowerCase().includes(normalizedSearch)
          || permission.key.toLowerCase().includes(normalizedSearch)
          || permission.label.toLowerCase().includes(normalizedSearch)
          || permission.description.toLowerCase().includes(normalizedSearch)
        )),
      }))
      .filter((group) => group.permissions.length > 0);
  }, [normalizedSearch]);

  const uncataloguedPermissions = useMemo(
    () => assignedPermissions.filter((permission) => !KNOWN_PERMISSION_KEYS.has(permission)).sort(),
    [assignedPermissions],
  );

  const updatePermission = (permissionKey: string, enabled: boolean) => {
    updatePermissions.mutate({
      id: employee.id,
      permissionStrings: nextPermissions(assignedPermissions, permissionKey, enabled),
      permissionKey,
    });
  };

  const updateGroupPermissions = (groupId: string, permissionKeys: string[], enabled: boolean) => {
    updatePermissions.mutate({
      id: employee.id,
      permissionStrings: nextGroupPermissions(assignedPermissions, permissionKeys, enabled),
      permissionKey: `group:${groupId}`,
    });
  };

  const addCustomPermission = () => {
    const permissionKey = normalizePermission(customPermission);
    if (!permissionKey || assignedSet.has(permissionKey)) return;
    updatePermissions.mutate({
      id: employee.id,
      permissionStrings: [...assignedPermissions, permissionKey].sort(),
      permissionKey,
    });
    setCustomPermission('');
  };

  const cataloguedAssignedCount = assignedPermissions.length - uncataloguedPermissions.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-md border bg-background p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-emerald-50 p-2 text-emerald-700">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold">Permission Strings</h2>
            <div className="mt-1 flex flex-wrap gap-1.5">
              <Badge variant="outline">{assignedPermissions.length} assigned</Badge>
              <Badge variant="outline">{cataloguedAssignedCount} catalogued</Badge>
              {uncataloguedPermissions.length > 0 && (
                <Badge variant="outline">{uncataloguedPermissions.length} custom</Badge>
              )}
            </div>
          </div>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search permissions"
            className="h-8 pl-7"
          />
        </div>
      </div>

      <div className="space-y-3">
        {visibleGroups.map((group) => {
          const assignedGroupCount = group.permissions.filter((permission) => assignedSet.has(permission.key)).length;
          const allGroupPermissionsAssigned = assignedGroupCount === group.permissions.length;
          const groupPendingKey = `group:${group.id}`;
          const isGroupPending = updatePermissions.isPending && pendingPermission === groupPendingKey;

          return (
            <section key={group.id} className="rounded-md border bg-background">
              <div className="grid gap-3 border-b px-4 py-3 sm:grid-cols-[1fr_auto] sm:items-center">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold">{group.label}</h3>
                    <Badge variant="secondary" className="rounded-sm">
                      {assignedGroupCount}/{group.permissions.length}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{group.description}</p>
                </div>
                <div className="flex items-center justify-end gap-2">
                  {isGroupPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                  <Switch
                    checked={allGroupPermissionsAssigned}
                    onCheckedChange={(enabled) => updateGroupPermissions(
                      group.id,
                      group.permissions.map((permission) => permission.key),
                      enabled,
                    )}
                    disabled={updatePermissions.isPending}
                    aria-label={`${group.label} section permissions`}
                  />
                </div>
              </div>
              <div className="divide-y">
                {group.permissions.map((permission) => {
                  const checked = assignedSet.has(permission.key);
                  const isPending = updatePermissions.isPending && pendingPermission === permission.key;

                  return (
                    <div key={permission.key} className="grid gap-3 px-4 py-3 sm:grid-cols-[1fr_auto] sm:items-center">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium">{permission.label}</span>
                          <code className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                            {permission.key}
                          </code>
                          {permission.level === 'elevated' && (
                            <Badge variant="outline" className="rounded-sm border-amber-300 text-amber-700">
                              Elevated
                            </Badge>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{permission.description}</p>
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                        <Switch
                          checked={checked}
                          onCheckedChange={(enabled) => updatePermission(permission.key, enabled)}
                          disabled={updatePermissions.isPending}
                          aria-label={`${permission.label} permission`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}

        {visibleGroups.length === 0 && (
          <div className="rounded-md border border-dashed py-8 text-center text-sm text-muted-foreground">
            No permissions match the current search.
          </div>
        )}
      </div>

      <div className="rounded-md border bg-background p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <div>
            <Label htmlFor="custom-permission" className="text-sm font-medium">Custom Permission</Label>
            <Input
              id="custom-permission"
              value={customPermission}
              onChange={(event) => setCustomPermission(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') addCustomPermission();
              }}
              placeholder="model.action"
              className="mt-1"
            />
          </div>
          <Button
            type="button"
            onClick={addCustomPermission}
            disabled={updatePermissions.isPending || !normalizePermission(customPermission) || assignedSet.has(normalizePermission(customPermission))}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add
          </Button>
        </div>

        {uncataloguedPermissions.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {uncataloguedPermissions.map((permission) => {
              const isPending = updatePermissions.isPending && pendingPermission === permission;
              return (
                <Badge
                  key={permission}
                  variant="outline"
                  className={cn('gap-1 rounded-sm py-1 font-mono', isPending && 'opacity-60')}
                >
                  {permission}
                  <button
                    type="button"
                    onClick={() => updatePermission(permission, false)}
                    disabled={updatePermissions.isPending}
                    className="ml-1 rounded-sm hover:bg-muted"
                    aria-label={`Remove ${permission}`}
                  >
                    {isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                  </button>
                </Badge>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
